import React, {FC, useEffect, useState} from 'react';
import {ContractsServices} from "./services/ContractsServices";
import {Contract, ContractsProps} from "./types";
import LoadingOverlay from "../../components/LoadingOverlay";
import {toast} from "react-toastify";
import logoImage from '../../assets/tecpay-no-bg.png';
import {
  Container,
  Description,
  ContractTitle,
  ContractCard,
  PdfContainer,
  ContractsContainer,
  PdfIframe,
  ErrorContainer,
  ButtonsContainer,
  DoubtsButton,
  Header,
  CheckboxContainer,
  CheckboxInput,
  CheckboxLabel,
  ProgressText,
  ContinueButton,
  LogoContainer,
  Logo
} from "./styles";

const CONTRACTS_ENABLED_DOMAINS = [
  'portal.valori.com.vc',
  'localhost',
  'portalhml.valori.com.vc',
  'happy-mushroom-0f6683910.4.azurestaticapps.net',
];

const Contracts: FC<ContractsProps> = ({ children }) => {
  const contractsServices = new ContractsServices();
  const isSupportAccess = localStorage.getItem('grantType') === 'support_access';
  const isContractsEnabledDomain = CONTRACTS_ENABLED_DOMAINS.includes(window.location.hostname);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [areAllContractsSigned, setAreAllContractsSigned] = useState<boolean>(false);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [pdfUrls, setPdfUrls] = useState<Map<string, string>>(new Map());
  const [acceptedContracts, setAcceptedContracts] = useState<Set<string>>(new Set());

  const fetchUnsignedContractsIds = async (): Promise<string[]> => {
    try {
      setIsLoading(true);
      return await contractsServices.getUnsignedContracts();
    } catch (error) {
      console.error(error);
      return []; // Retorna array vazio em caso de erro
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContracts = async (unsignedContractsIds: string[]): Promise<Contract[]> => {
    try {
      setIsLoading(true);
      return await contractsServices.getContracts(unsignedContractsIds);
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Converte base64 para Blob URL
  const createPdfBlobUrl = (base64Content: string): string | null => {
    if (!base64Content) return null;

    try {
      // Remove prefixo se existir
      const cleanBase64 = base64Content.replace(/^data:application\/pdf;base64,/, '');

      // Converte base64 para binary
      const binaryString = window.atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Cria blob
      const blob = new Blob([bytes], { type: 'application/pdf' });

      // Retorna URL do blob
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Erro ao criar blob URL:', error);
      return null;
    }
  };

  const handleCheckboxChange = (contractId: string, isChecked: boolean) => {
    setAcceptedContracts(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(contractId);
      } else {
        newSet.delete(contractId);
      }
      return newSet;
    });
  };

  const handleContinue = async () => {
    try {
      setIsLoading(true);

      // Converte o Set em array para iterar
      const contractsToAccept = Array.from(acceptedContracts);

      // Processa cada contrato aceito sequencialmente
      for (const contractId of contractsToAccept) {
        try {
          const response = await contractsServices.updateContractsStatus(contractId);

          if (response) {
            // Remove o PDF da memória
            const urlToRevoke = pdfUrls.get(contractId);
            if (urlToRevoke) {
              URL.revokeObjectURL(urlToRevoke);
            }
          }
        } catch (error) {
          console.error(`Erro ao aceitar contrato ${contractId}:`, error);
          toast.error(`Erro ao processar um dos contratos. Tente novamente.`);
          setIsLoading(false);
          return;
        }
      }

      toast.success("Todos os contratos foram aceitos com sucesso!");
      setAreAllContractsSigned(true);

    } catch (error) {
      console.error(error);
      toast.error("Erro ao processar contratos. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  function handleDoubts() {
    window.open("https://api.whatsapp.com/send/?phone=556240001708&text&type=phone_number&app_absent=0", "_blank");
  }

  useEffect(() => {
    let mounted = true;

    fetchUnsignedContractsIds().then(response => {
      if (!mounted) return;

      if (!response || response.length === 0) {
        setAreAllContractsSigned(true);
      } else {
        fetchContracts(response).then(contractsData => {
          if (mounted && contractsData) {
            setContracts(contractsData);

            const newPdfUrls = new Map<string, string>();
            contractsData.forEach(contract => {
              if (contract.base64Content) {
                const url = createPdfBlobUrl(contract.base64Content);
                if (url) {
                  newPdfUrls.set(contract.id, url);
                }
              }
            });
            setPdfUrls(newPdfUrls);
          }
        });
      }
    });

    return () => { mounted = false };
  }, []);

  useEffect(() => {
    return () => {
      pdfUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [pdfUrls]);

  const allContractsAccepted = acceptedContracts.size === contracts.length && contracts.length > 0;

  return (
    <>
      <LoadingOverlay show={isLoading} />
      {(!isContractsEnabledDomain || areAllContractsSigned || isSupportAccess) ? (
        children
      ) : (
        <Container>
          <LogoContainer>
            <Logo src={logoImage} alt="Logo Valori" />
          </LogoContainer>
          {contracts.length > 0 && (
            <>
              <Header>
                <h1>Termos Pendentes de Assinatura</h1>
              </Header>

              <Description>
                Para continuar utilizando o sistema, você precisa ler e aceitar todos os contratos abaixo:
              </Description>

              <ContractsContainer>
                {contracts.map((contract, index) => {
                  const pdfUrl = pdfUrls.get(contract.id);
                  const isAccepted = acceptedContracts.has(contract.id);

                  return (
                    <ContractCard key={contract.id} $isAccepted={isAccepted}>
                      <ContractTitle>
                        Termo {index + 1} de {contracts.length}: {contract.fileName}
                      </ContractTitle>

                      <PdfContainer>
                        {pdfUrl ? (
                          <PdfIframe
                            src={pdfUrl}
                            width="100%"
                            height="600px"
                            title={contract.fileName}
                          />
                        ) : (
                          <ErrorContainer>
                            <p>Erro ao carregar visualização do PDF</p>
                          </ErrorContainer>
                        )}
                      </PdfContainer>

                      <CheckboxContainer>
                        <CheckboxInput
                          type="checkbox"
                          id={`contract-${contract.id}`}
                          checked={isAccepted}
                          onChange={(e) => handleCheckboxChange(contract.id, e.target.checked)}
                        />
                        <CheckboxLabel htmlFor={`contract-${contract.id}`}>
                          Li e aceito os termos deste contrato
                        </CheckboxLabel>
                      </CheckboxContainer>
                    </ContractCard>
                  );
                })}
              </ContractsContainer>

              <ProgressText>
                {acceptedContracts.size} de {contracts.length} contrato(s) aceito(s)
              </ProgressText>

              <ButtonsContainer>
                <DoubtsButton onClick={handleDoubts}>
                  Dúvidas
                </DoubtsButton>

                <ContinueButton
                  onClick={handleContinue}
                  disabled={!allContractsAccepted}
                >
                  Continuar
                </ContinueButton>
              </ButtonsContainer>
            </>
          )}
        </Container>
      )}
    </>
  );
}

export default Contracts;
